/**
 * Practice-fee explain screen for private patients
 *
 * Route: `/webAppOnboarding/insurance-blocked-practice-fee-process-overview`. The practice charges private
 * patients and self-payers a fee for app use and settles it with the patient directly; Physiofit bills
 * nothing. Reached only when the practice enabled `privatePatientSettings.feeEnabled` and the insurer is
 * private, a decision persisted as `insuranceInfo.practiceChargesFee` at selection time.
 *
 * Guards:
 * - Route: `OnboardingSignUpGateLayout` → `OnboardingAuthGuard` → `OnboardingCompletedGuard`.
 * - Auth: Firebase user required via `OnboardingAuthGuard`.
 * - Onboarding state: completed patients redirected to install by `OnboardingCompletedGuard`.
 * - Screen-local: `useInsuranceExplainBillingGuard('blocked', { blockedExplainRouteSegment: practice-fee segment })`.
 */

import { PATIENT_TRACKING_EVENTS } from '@physiofit-digital/physiofit_types';
import { usePostHog } from '@posthog/react';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { space } from '../../../styles/onboardingUiTokens';
import { Notice } from '../components/elements/Notice';
import { OnboardingBody } from '../components/layout/OnboardingBody';
import { OnboardingFooter } from '../components/layout/OnboardingFooter';
import { OnboardingHeader } from '../components/layout/OnboardingHeader';
import { OnboardingLayout } from '../components/layout/OnboardingLayout';
import { H1 } from '../components/typography/HeadingLevels';
import { useInsuranceExplainBillingGuard } from '../hooks/useInsuranceExplainBillingGuard';
import { BLOCKED_PRACTICE_FEE_EXPLAIN_ROUTE_SEGMENT } from '../insurance/config';
import { resolveInsuranceOnboardingContext } from '../insurance/routing';
import { editSignedUpPatient } from '../services/editSignedUpPatient';
import { useSaveOnboardingStep } from '../services/onboardingProgress';
import { getSignedUpPatient, useOnboardingStore } from '../store/onboardingStore';

export const ONBOARDING_SCREEN_META = {
  requiresFirebaseSession: true,
} as const;

export function WebOnboardingInsuranceBlockedPracticeFeeProcessOverviewScreen() {
  useSaveOnboardingStep('insurance_blocked_practice_fee_process_overview');
  const navigate = useNavigate();
  const posthog = usePostHog();
  const signedUpPatient = useOnboardingStore(getSignedUpPatient);

  const ctx = useMemo(
    () => resolveInsuranceOnboardingContext({ source: 'patient', patient: signedUpPatient }),
    [signedUpPatient],
  );

  useInsuranceExplainBillingGuard('blocked', {
    blockedExplainRouteSegment: BLOCKED_PRACTICE_FEE_EXPLAIN_ROUTE_SEGMENT,
  });

  const handleBack = useCallback(() => {
    navigate('/webAppOnboarding/insurance-select');
  }, [navigate]);

  const handleForward = () => {
    posthog.capture(PATIENT_TRACKING_EVENTS.web_insurance_process_overview_completed, {
      processType: 'blockedPracticeFee',
      billingType: signedUpPatient?.webAppOnboarding?.insuranceInfo?.billingType,
    });
    // Best effort: the acknowledgement is a record, never a gate, so neither a slow write nor a
    // failed one may hold up the patient.
    void editSignedUpPatient({
      'webAppOnboarding.flags.practiceFeeAcknowledgedDate': new Date().toISOString(),
    }).catch((error: unknown) => {
      if (import.meta.env.DEV) {
        console.error('[onboarding] practice fee acknowledgement write failed', error);
      }
    });
    if (ctx.forwardPath) {
      navigate(ctx.forwardPath);
    }
  };

  return (
    <OnboardingLayout currentStep={4} totalSteps={5} onBack={handleBack} onForward={handleForward}>
      <OnboardingHeader />
      <OnboardingBody>
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.lg, paddingBottom: space.xl }}>
          <H1>Die App wird über deine Praxis abgerechnet.</H1>
          <Notice preset="info">
            Deine Praxis erhebt zur Nutzung der Physiofit App eine Gebühr. Sprich sie am besten direkt auf die
            Konditionen an. Im nächsten Schritt installierst du die App.
          </Notice>
        </div>
      </OnboardingBody>
      <OnboardingFooter />
    </OnboardingLayout>
  );
}
